import { requestJson } from "./apiClient";

const BASE = "/api/food";

export async function fetchFoodSummary(options = {}) {
  return requestJson(`${BASE}/summary`, { signal: options.signal });
}

export async function fetchFoodSources(options = {}) {
  return requestJson(`${BASE}/sources`, { signal: options.signal });
}

export async function fetchFoodStores(options = {}) {
  return requestJson(`${BASE}/stores`, { signal: options.signal });
}

export async function fetchFoodProducts(options = {}) {
  return requestJson(`${BASE}/products`, { signal: options.signal });
}

export async function fetchFoodBaskets(options = {}) {
  return requestJson(`${BASE}/baskets`, { signal: options.signal });
}

export async function fetchFoodMapFeatures(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/map/features${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodMapStores(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/map/stores${qs ? `?${qs}` : ""}`, { signal: options.signal });
}

export async function fetchFoodMapAccessAreas(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/map/access-areas${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodMapSummary(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/map/summary${qs ? `?${qs}` : ""}`, { signal: options.signal });
}

export async function fetchFoodMapAreaDetail(areaId, options = {}) {
  return requestJson(`${BASE}/map/areas/${areaId}`, { signal: options.signal });
}

export async function fetchFoodWalkingIsochrones(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.department) search.set("department", params.department);
  if (params.municipality) search.set("municipality", params.municipality);
  if (params.storeId) search.set("storeId", params.storeId);
  if (params.timeMinutes != null) search.set("timeMinutes", String(params.timeMinutes));
  if (params.provider) search.set("provider", params.provider);
  if (params.bbox) search.set("bbox", params.bbox);
  const qs = search.toString();
  return requestJson(`${BASE}/map/walking-isochrones${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function postFoodWalkingIsochronesDryRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/map/walking-isochrones/generate/dry-run`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodWalkingIsochronesRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/map/walking-isochrones/generate/run`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodWalkingAccessCalculate(payload = {}, options = {}) {
  return requestJson(`${BASE}/map/walking-access/calculate`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function fetchFreshFoodMapPoints(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.category) search.set("category", params.category);
  if (params.categories) search.set("categories", params.categories);
  if (params.municipality) search.set("municipality", params.municipality);
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.confidence) search.set("confidence", params.confidence);
  if (params.bbox) search.set("bbox", params.bbox);
  if (params.includeNeedsReview) search.set("includeNeedsReview", "true");
  const qs = search.toString();
  return requestJson(`${BASE}/map/fresh-food-points${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFreshFoodSummary(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  const qs = search.toString();
  return requestJson(`${BASE}/fresh-pois/summary${qs ? `?${qs}` : ""}`, { signal: options.signal });
}

export async function fetchFreshFoodCategories(options = {}) {
  return requestJson(`${BASE}/fresh-pois/categories`, { signal: options.signal });
}

export async function postFreshFoodIngestDryRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/fresh-pois/ingest/dry-run`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFreshFoodIngestRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/fresh-pois/ingest/run`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodConfidenceRecalculateDryRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/confidence/recalculate/dry-run`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodConfidenceRecalculateRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/confidence/recalculate/run`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function fetchFoodConfidenceSummary(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.category) search.set("category", params.category);
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.municipality) search.set("municipality", params.municipality);
  const qs = search.toString();
  return requestJson(`${BASE}/confidence/summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodConfidenceByPointId(pointId, options = {}) {
  return requestJson(`${BASE}/confidence/${encodeURIComponent(pointId)}`, {
    signal: options.signal,
  });
}

export async function fetchFoodWalkingAccessProviderStatus(options = {}) {
  return requestJson(`${BASE}/map/walking-isochrones/provider-status`, {
    signal: options.signal,
  });
}

export async function fetchFoodWalkingIsochroneGenerationRuns(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  return requestJson(`${BASE}/map/walking-isochrones/generation-runs${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAccessNearby(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/access/nearby${qs ? `?${qs}` : ""}`, { signal: options.signal });
}

export async function calculateFoodAccessAreas(payload = {}, options = {}) {
  return requestJson(`${BASE}/map/access-areas/calculate`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchLatestFoodAccessAreas(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.department) search.set("department", params.department);
  if (params.municipality) search.set("municipality", params.municipality);
  if (params.region) search.set("region", params.region);
  if (params.areaType) search.set("areaType", params.areaType);
  if (params.accessStatus) search.set("accessStatus", params.accessStatus);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.bbox) search.set("bbox", params.bbox);
  const qs = search.toString();
  return requestJson(`${BASE}/map/access-areas/latest${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAccessCalculationRuns(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.department) search.set("department", params.department);
  if (params.areaType) search.set("areaType", params.areaType);
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  return requestJson(`${BASE}/map/access-areas/calculation-runs${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAccessCalculationVisibility(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.department) search.set("department", params.department);
  if (params.areaType) search.set("areaType", params.areaType);
  const qs = search.toString();
  return requestJson(`${BASE}/map/access-areas/calculation-visibility${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAccessCoverageDashboard(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.department) search.set("department", params.department);
  if (params.municipality) search.set("municipality", params.municipality);
  if (params.region) search.set("region", params.region);
  if (params.areaType) search.set("areaType", params.areaType);
  if (params.bbox) search.set("bbox", params.bbox);
  if (params.includeAdminMetrics != null) {
    search.set("includeAdminMetrics", String(params.includeAdminMetrics));
  }
  const qs = search.toString();
  return requestJson(`${BASE}/map/coverage-dashboard${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAccessRoadmapStatus(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.department) search.set("department", params.department);
  if (params.municipality) search.set("municipality", params.municipality);
  if (params.region) search.set("region", params.region);
  if (params.areaType) search.set("areaType", params.areaType);
  if (params.includeAdminLinks != null) {
    search.set("includeAdminLinks", String(params.includeAdminLinks));
  }
  const qs = search.toString();
  return requestJson(`${BASE}/map/roadmap-status${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodSupermarketRefreshStatus(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.department) search.set("department", params.department);
  if (params.areaType) search.set("areaType", params.areaType);
  const qs = search.toString();
  return requestJson(`${BASE}/supermarkets/refresh/status${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

function buildManualRefreshWorkflowSearch(params = {}) {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.department) search.set("department", params.department);
  if (params.municipality) search.set("municipality", params.municipality);
  if (params.region) search.set("region", params.region);
  if (params.areaType) search.set("areaType", params.areaType);
  if (params.includeAdminLinks != null) {
    search.set("includeAdminLinks", String(params.includeAdminLinks));
  }
  if (params.chainImportFile) search.set("chainImportFile", params.chainImportFile);
  return search.toString();
}

export async function fetchFoodManualRefreshWorkflowStatus(params = {}, options = {}) {
  const qs = buildManualRefreshWorkflowSearch(params);
  return requestJson(`${BASE}/supermarkets/manual-refresh/workflow-status${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function postFoodManualRefreshWorkflowStepDryRun(stepKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/supermarkets/manual-refresh/${encodeURIComponent(stepKey)}/dry-run`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodManualRefreshWorkflowStepApply(stepKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/supermarkets/manual-refresh/${encodeURIComponent(stepKey)}/apply`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodManualRefreshWorkflowStepValidate(stepKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/supermarkets/manual-refresh/${encodeURIComponent(stepKey)}/validate`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function fetchFoodOsmRefreshSchedulerStatus(options = {}) {
  return requestJson(`${BASE}/supermarkets/osm-refresh-scheduler/status`, {
    signal: options.signal,
  });
}

export async function postFoodOsmRefreshSchedulerEnable(payload = {}, options = {}) {
  return requestJson(`${BASE}/supermarkets/osm-refresh-scheduler/enable`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodOsmRefreshSchedulerDisable(payload = {}, options = {}) {
  return requestJson(`${BASE}/supermarkets/osm-refresh-scheduler/disable`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodOsmRefreshSchedulerRunNowDryRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/supermarkets/osm-refresh-scheduler/run-now-dry-run`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function postFoodOsmRefreshSchedulerRunNow(payload = {}, options = {}) {
  return requestJson(`${BASE}/supermarkets/osm-refresh-scheduler/run-now`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function fetchFoodOsmRefreshSchedulerRuns(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  return requestJson(`${BASE}/supermarkets/osm-refresh-scheduler/runs${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodChainLocatorAuditStatus(options = {}) {
  const search = new URLSearchParams();
  if (options.includeAdminLinks) search.set("includeAdminLinks", "true");
  const qs = search.toString();
  return requestJson(`${BASE}/branch-locators/audit/status${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodChainLocatorAuditDetail(sourceKey, options = {}) {
  const search = new URLSearchParams();
  if (options.includeAdminLinks) search.set("includeAdminLinks", "true");
  const qs = search.toString();
  return requestJson(
    `${BASE}/branch-locators/audit/status/${encodeURIComponent(sourceKey)}${qs ? `?${qs}` : ""}`,
    { signal: options.signal }
  );
}

export async function postFoodChainLocatorAuditMarkReviewed(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/branch-locators/audit/${encodeURIComponent(sourceKey)}/mark-reviewed`, {
    method: "POST",
    body: payload,
    signal: options.signal,
  });
}

export async function fetchFoodAdminOperationRuns(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.operationType) search.set("operationType", params.operationType);
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  return requestJson(`${BASE}/admin/operations/runs${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAdminOperationRun(runId, options = {}) {
  return requestJson(`${BASE}/admin/operations/runs/${encodeURIComponent(runId)}`, {
    signal: options.signal,
  });
}

export async function postFoodAdminOsmIngestDryRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/admin/operations/osm-ingest/dry-run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodAdminOsmIngestRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/admin/operations/osm-ingest/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodAdminAccessRecalculationDryRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/admin/operations/access-recalculation/dry-run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodAdminAccessRecalculationRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/admin/operations/access-recalculation/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodSourceDiscovery(options = {}) {
  return requestJson(`${BASE}/source-discovery`, { signal: options.signal });
}

export async function fetchFoodSourceDiscoveryByKey(sourceKey, options = {}) {
  return requestJson(`${BASE}/source-discovery/${encodeURIComponent(sourceKey)}`, {
    signal: options.signal,
  });
}

export async function updateFoodSourceDiscovery(sourceKey, payload, options = {}) {
  return requestJson(`${BASE}/source-discovery/${encodeURIComponent(sourceKey)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodRetailSourceSelection(options = {}) {
  return requestJson(`${BASE}/retail/source-selection`, { signal: options.signal });
}

export async function fetchFoodSecondRetailSourceSelection(options = {}) {
  return requestJson(`${BASE}/retail/second-source-selection`, { signal: options.signal });
}

export async function runFoodRetailIngestion(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/ingestion/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodRetailIngestionRuns(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.status) search.set("status", params.status);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  if (params.since) search.set("since", params.since);
  if (params.dryRun != null) search.set("dryRun", String(params.dryRun));
  const qs = search.toString();
  return requestJson(`${BASE}/retail/ingestion/runs${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchLatestFoodRetailPrices(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.productName) search.set("productName", params.productName);
  if (params.brand) search.set("brand", params.brand);
  if (params.storeId) search.set("storeId", params.storeId);
  if (params.since) search.set("since", params.since);
  if (params.onlyAvailable != null) search.set("onlyAvailable", String(params.onlyAvailable));
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  const qs = search.toString();
  return requestJson(`${BASE}/retail-prices/latest${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodRetailIngestionReadiness(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/retail/ingestion/readiness/${encodeURIComponent(sourceKey)}`,
    { signal: options.signal }
  );
}

export async function fetchFoodLiveCategoryUrls(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.reviewStatus) search.set("reviewStatus", params.reviewStatus);
  const qs = search.toString();
  return requestJson(`${BASE}/retail/live-category-urls${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function validateFoodLiveCategoryUrls(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/live-category-urls/validate`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function previewFoodRetailNormalization(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/normalization/preview`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function applyFoodRetailNormalization(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/normalization/apply`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodRetailNormalizationReviewQueue(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.status) search.set("status", params.status);
  if (params.includeUnmatched != null) {
    search.set("includeUnmatched", String(params.includeUnmatched));
  }
  if (params.likelyStapleOnly != null) {
    search.set("likelyStapleOnly", String(params.likelyStapleOnly));
  }
  if (params.priorityTier) search.set("priorityTier", params.priorityTier);
  if (params.productHint) search.set("productHint", params.productHint);
  if (params.sort) search.set("sort", params.sort);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  if (params.productName) search.set("productName", params.productName);
  const qs = search.toString();
  return requestJson(`${BASE}/retail/normalization/review-queue${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodRetailManualReviewQueue(params = {}, options = {}) {
  return fetchFoodRetailNormalizationReviewQueue(params, options);
}

export async function fetchFoodRetailManualReviewSummary(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  const qs = search.toString();
  return requestJson(`${BASE}/retail/normalization/review-summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function postFoodRetailManualMatch(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/normalization/manual-match`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodRetailManualReject(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/normalization/reject`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodRetailBulkReview(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/normalization/bulk-review`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodRetailSourceEnablementStatus(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/retail/sources/${encodeURIComponent(sourceKey)}/enablement-status`,
    { signal: options.signal }
  );
}

export async function enableFoodRetailSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/sources/${encodeURIComponent(sourceKey)}/enable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function disableFoodRetailSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/sources/${encodeURIComponent(sourceKey)}/disable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodSipsaSourceEnablementStatus(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/sipsa/sources/${encodeURIComponent(sourceKey)}/enablement-status`,
    { signal: options.signal }
  );
}

export async function enableFoodSipsaSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/sipsa/sources/${encodeURIComponent(sourceKey)}/enable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function disableFoodSipsaSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/sipsa/sources/${encodeURIComponent(sourceKey)}/disable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodSipsaSourceIngestionReadiness(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/sipsa/sources/${encodeURIComponent(sourceKey)}/ingestion/readiness`,
    { signal: options.signal }
  );
}

export async function runFoodSipsaSourceIngestion(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/sipsa/sources/${encodeURIComponent(sourceKey)}/ingestion/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodInputReferenceSourceEnablementStatus(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/reference/sources/${encodeURIComponent(sourceKey)}/enablement-status`,
    { signal: options.signal }
  );
}

export async function enableFoodInputReferenceSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/reference/sources/${encodeURIComponent(sourceKey)}/enable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function disableFoodInputReferenceSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/reference/sources/${encodeURIComponent(sourceKey)}/disable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodInputReferenceSourceIngestionReadiness(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/reference/sources/${encodeURIComponent(sourceKey)}/ingestion/readiness`,
    { signal: options.signal }
  );
}

export async function runFoodInputReferenceSourceIngestion(sourceKey, payload = {}, options = {}) {
  return requestJson(
    `${BASE}/reference/sources/${encodeURIComponent(sourceKey)}/ingestion/run`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  );
}

export async function fetchFoodFuelReferenceSourceStatus(options = {}) {
  return requestJson(`${BASE}/reference/fuel/source-status`, { signal: options.signal });
}

export async function fetchFoodOsmPoiSourceEnablementStatus(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/osm-poi/sources/${encodeURIComponent(sourceKey)}/enablement-status`,
    { signal: options.signal }
  );
}

export async function enableFoodOsmPoiSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/osm-poi/sources/${encodeURIComponent(sourceKey)}/enable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function disableFoodOsmPoiSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/osm-poi/sources/${encodeURIComponent(sourceKey)}/disable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodOsmPoiSourceIngestionReadiness(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/osm-poi/sources/${encodeURIComponent(sourceKey)}/ingestion/readiness`,
    { signal: options.signal }
  );
}

export async function runFoodOsmPoiSourceIngestion(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/osm-poi/sources/${encodeURIComponent(sourceKey)}/ingestion/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodBranchLocatorSourceEnablementStatus(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/branch-locator/sources/${encodeURIComponent(sourceKey)}/enablement-status`,
    { signal: options.signal }
  );
}

export async function enableFoodBranchLocatorSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/branch-locator/sources/${encodeURIComponent(sourceKey)}/enable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function disableFoodBranchLocatorSource(sourceKey, payload = {}, options = {}) {
  return requestJson(`${BASE}/branch-locator/sources/${encodeURIComponent(sourceKey)}/disable`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodBranchLocatorSourceIngestionReadiness(sourceKey, options = {}) {
  return requestJson(
    `${BASE}/branch-locator/sources/${encodeURIComponent(sourceKey)}/ingestion/readiness`,
    { signal: options.signal }
  );
}

export async function runFoodBranchLocatorSourceIngestion(sourceKey, payload = {}, options = {}) {
  return requestJson(
    `${BASE}/branch-locator/sources/${encodeURIComponent(sourceKey)}/ingestion/run`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  );
}

export async function runFoodNormalization(payload = {}, options = {}) {
  return requestJson(`${BASE}/normalization/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodNormalizationReviewQueue(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.status) search.set("status", params.status);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  if (params.productName) search.set("productName", params.productName);
  const qs = search.toString();
  return requestJson(`${BASE}/normalization/review-queue${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function updateFoodNormalizationObservation(observationId, payload, options = {}) {
  return requestJson(
    `${BASE}/normalization/observations/${encodeURIComponent(observationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  );
}

export async function fetchFoodBasketSummary(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  const qs = search.toString();
  return requestJson(`${BASE}/basket-intelligence/summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function calculateFoodBasket(payload = {}, options = {}) {
  return requestJson(`${BASE}/basket-intelligence/calculate`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function calculateFoodBaskets(payload = {}, options = {}) {
  return requestJson(`${BASE}/baskets/calculate`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchLatestFoodBasketResult(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.basketId) search.set("basketId", params.basketId);
  const qs = search.toString();
  return requestJson(`${BASE}/baskets/results/latest${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodBasketsComparison(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKeys?.length) {
    search.set("sourceKeys", params.sourceKeys.join(","));
  }
  if (params.basketId) search.set("basketId", params.basketId);
  if (params.basketName) search.set("basketName", params.basketName);
  if (params.freshnessWindowHours != null) {
    search.set("freshnessWindowHours", String(params.freshnessWindowHours));
  }
  if (params.includeItems === false) search.set("includeItems", "false");
  const qs = search.toString();
  return requestJson(`${BASE}/baskets/comparison${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodBasketResults(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.basketId) search.set("basketId", params.basketId);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  const qs = search.toString();
  return requestJson(`${BASE}/baskets/results${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodRetailFreshnessSummary(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.basketId) search.set("basketId", params.basketId);
  if (params.basketName) search.set("basketName", params.basketName);
  if (params.freshnessWindowHours != null) {
    search.set("freshnessWindowHours", String(params.freshnessWindowHours));
  }
  const qs = search.toString();
  return requestJson(`${BASE}/retail/freshness-summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodRetailPackageReadiness(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.onlyProblems != null) search.set("onlyProblems", String(params.onlyProblems));
  const qs = search.toString();
  return requestJson(`${BASE}/retail/package-readiness${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function runFoodAraPackageParsingBackfill(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/package-parsing/backfill`, {
    method: "POST",
    body: JSON.stringify({
      sourceKey: payload.sourceKey,
      limit: payload.limit,
      dryRun: payload.dryRun !== false,
      overwrite: payload.overwrite === true,
    }),
    signal: options.signal,
  });
}

export async function fetchFoodBasketCoverageBaseline(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.basketId) search.set("basketId", params.basketId);
  if (params.basketName) search.set("basketName", params.basketName);
  if (params.freshnessWindowHours != null) {
    search.set("freshnessWindowHours", String(params.freshnessWindowHours));
  }
  const qs = search.toString();
  return requestJson(`${BASE}/baskets/coverage-baseline${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAraRefreshStatus(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.basketName) search.set("basketName", params.basketName);
  if (params.freshnessWindowHours != null) {
    search.set("freshnessWindowHours", String(params.freshnessWindowHours));
  }
  const qs = search.toString();
  return requestJson(`${BASE}/ara/refresh-status${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAraSustainabilityReport(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.basketName) search.set("basketName", params.basketName);
  if (params.freshnessWindowHours != null) {
    search.set("freshnessWindowHours", String(params.freshnessWindowHours));
  }
  const qs = search.toString();
  return requestJson(`${BASE}/ara/sustainability-report${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAraStressTest(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.basketName) search.set("basketName", params.basketName);
  if (params.freshnessWindowHours != null) {
    search.set("freshnessWindowHours", String(params.freshnessWindowHours));
  }
  const qs = search.toString();
  return requestJson(`${BASE}/ara/stress-test${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAraIngestionExpansionPlan(options = {}) {
  return requestJson(`${BASE}/ara/ingestion/expansion-plan`, {
    signal: options.signal,
  });
}

export async function expandFoodRetailIngestion(payload = {}, options = {}) {
  return requestJson(`${BASE}/retail/ingestion/expand`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function expandFoodAraIngestion(payload = {}, options = {}) {
  return requestJson(`${BASE}/ara/ingestion/expand`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodAraCityGateStrategy(options = {}) {
  return requestJson(`${BASE}/ara/city-gate-strategy`, {
    signal: options.signal,
  });
}

export async function fetchFoodRetailSourceAccessStrategy(sourceKey, options = {}) {
  return requestJson(`${BASE}/retail/sources/${encodeURIComponent(sourceKey)}/access-strategy`, {
    signal: options.signal,
  });
}

export async function fetchFoodBasketStapleTargetPlan(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.basketId) search.set("basketId", params.basketId);
  if (params.basketName) search.set("basketName", params.basketName);
  if (params.freshnessWindowHours != null) {
    search.set("freshnessWindowHours", String(params.freshnessWindowHours));
  }
  const qs = search.toString();
  return requestJson(`${BASE}/baskets/staple-target-plan${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodBasketSnapshots(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.basketId) search.set("basketId", params.basketId);
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.storeId) search.set("storeId", params.storeId);
  if (params.city) search.set("city", params.city);
  if (params.neighborhood) search.set("neighborhood", params.neighborhood);
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  return requestJson(`${BASE}/basket-intelligence/snapshots${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodBasketSnapshotById(snapshotId, options = {}) {
  return requestJson(
    `${BASE}/basket-intelligence/snapshots/${encodeURIComponent(snapshotId)}`,
    { signal: options.signal }
  );
}

export async function fetchFoodBasketHistory(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.basketId) search.set("basketId", params.basketId);
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.storeId) search.set("storeId", params.storeId);
  if (params.city) search.set("city", params.city);
  if (params.neighborhood) search.set("neighborhood", params.neighborhood);
  if (params.daysBack != null) search.set("daysBack", String(params.daysBack));
  const qs = search.toString();
  return requestJson(`${BASE}/basket-intelligence/history${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

function buildComparisonSearch(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== "") search.set(key, String(value));
  });
  return search.toString();
}

export async function fetchFoodComparisonSummary(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/comparison/summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodBasketComparison(params = {}, options = {}) {
  const qs = buildComparisonSearch({
    ...params,
    includeIncomplete: params.includeIncomplete ? "true" : undefined,
  });
  return requestJson(`${BASE}/comparison/baskets${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodBasketComparisonDetail(snapshotId, options = {}) {
  return requestJson(`${BASE}/comparison/baskets/${encodeURIComponent(snapshotId)}`, {
    signal: options.signal,
  });
}

export async function fetchFoodProductComparison(params = {}, options = {}) {
  const qs = buildComparisonSearch({
    ...params,
    includePromotions: params.includePromotions === false ? "false" : undefined,
  });
  return requestJson(`${BASE}/comparison/products${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodProductComparisonGrouped(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/comparison/products/grouped${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodPriceMovements(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/comparison/price-movements${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function runFoodWholesaleIngestion(payload = {}, options = {}) {
  return requestJson(`${BASE}/wholesale/ingestion/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodWholesaleDashboard(options = {}) {
  return requestJson(`${BASE}/wholesale/dashboard`, { signal: options.signal });
}

export async function fetchFoodWholesaleLatest(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/wholesale/prices/latest${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodWholesaleHistory(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/wholesale/prices/history${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodWholesaleTrends(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/wholesale/products/trends${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodWholesaleMarkets(options = {}) {
  return requestJson(`${BASE}/wholesale/markets`, { signal: options.signal });
}

export async function fetchFoodWholesaleSourceStatus(options = {}) {
  return requestJson(`${BASE}/wholesale/source-status`, { signal: options.signal });
}

export async function runFoodSupplyIngestion(payload = {}, options = {}) {
  return requestJson(`${BASE}/supply/ingestion/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function runFoodSupplyTrendCalculation(payload = {}, options = {}) {
  return requestJson(`${BASE}/supply/trends/calculate`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodSupplyDashboard(options = {}) {
  return requestJson(`${BASE}/supply/dashboard`, { signal: options.signal });
}

export async function fetchFoodSupplyLatest(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/supply/latest${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodSupplyHistory(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/supply/history${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodSupplyTrends(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/supply/trends${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodSupplyPressureSummary(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/supply/pressure-summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodSupplyPricePressure(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/supply/price-pressure${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodSupplyMarkets(options = {}) {
  return requestJson(`${BASE}/supply/markets`, { signal: options.signal });
}

export async function fetchFoodSupplySourceStatus(options = {}) {
  return requestJson(`${BASE}/supply/source-status`, { signal: options.signal });
}

export async function runFoodInputIngestion(payload = {}, options = {}) {
  return requestJson(`${BASE}/inputs/ingestion/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function runFoodInputTrendCalculation(payload = {}, options = {}) {
  return requestJson(`${BASE}/inputs/trends/calculate`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodInputDashboard(options = {}) {
  return requestJson(`${BASE}/inputs/dashboard`, { signal: options.signal });
}

export async function fetchFoodInputsLatest(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/inputs/latest${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodInputsHistory(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/inputs/history${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodInputTrends(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/inputs/trends${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodInputPressureSummary(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/inputs/pressure-summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodInputFoodPressure(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/inputs/food-pressure${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodInputSourceStatus(options = {}) {
  return requestJson(`${BASE}/inputs/source-status`, { signal: options.signal });
}

export async function fetchFoodRetailVsWholesale(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/retail-vs-wholesale${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodAlertsSummary(options = {}) {
  return requestJson(`${BASE}/alerts/summary`, { signal: options.signal });
}

export async function fetchFoodWatchlists(options = {}) {
  return requestJson(`${BASE}/watchlists`, { signal: options.signal });
}

export async function createFoodWatchlist(payload, options = {}) {
  return requestJson(`${BASE}/watchlists`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodWatchlist(watchlistId, options = {}) {
  return requestJson(`${BASE}/watchlists/${encodeURIComponent(watchlistId)}`, {
    signal: options.signal,
  });
}

export async function updateFoodWatchlist(watchlistId, payload, options = {}) {
  return requestJson(`${BASE}/watchlists/${encodeURIComponent(watchlistId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function deleteFoodWatchlist(watchlistId, options = {}) {
  return requestJson(`${BASE}/watchlists/${encodeURIComponent(watchlistId)}`, {
    method: "DELETE",
    signal: options.signal,
  });
}

export async function addFoodWatchlistItem(watchlistId, payload, options = {}) {
  return requestJson(`${BASE}/watchlists/${encodeURIComponent(watchlistId)}/items`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function updateFoodWatchlistItem(itemId, payload, options = {}) {
  return requestJson(`${BASE}/watchlists/items/${encodeURIComponent(itemId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function removeFoodWatchlistItem(itemId, options = {}) {
  return requestJson(`${BASE}/watchlists/items/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
    signal: options.signal,
  });
}

export async function fetchFoodAlertRules(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/alerts/rules${qs ? `?${qs}` : ""}`, { signal: options.signal });
}

export async function createFoodAlertRule(payload, options = {}) {
  return requestJson(`${BASE}/alerts/rules`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function updateFoodAlertRule(alertRuleId, payload, options = {}) {
  return requestJson(`${BASE}/alerts/rules/${encodeURIComponent(alertRuleId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function deleteFoodAlertRule(alertRuleId, options = {}) {
  return requestJson(`${BASE}/alerts/rules/${encodeURIComponent(alertRuleId)}`, {
    method: "DELETE",
    signal: options.signal,
  });
}

export async function enableFoodAlertRule(alertRuleId, options = {}) {
  return requestJson(`${BASE}/alerts/rules/${encodeURIComponent(alertRuleId)}/enable`, {
    method: "POST",
    signal: options.signal,
  });
}

export async function disableFoodAlertRule(alertRuleId, options = {}) {
  return requestJson(`${BASE}/alerts/rules/${encodeURIComponent(alertRuleId)}/disable`, {
    method: "POST",
    signal: options.signal,
  });
}

export async function fetchFoodAlertEvents(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/alerts/events${qs ? `?${qs}` : ""}`, { signal: options.signal });
}

export async function acknowledgeFoodAlertEvent(eventId, options = {}) {
  return requestJson(`${BASE}/alerts/events/${encodeURIComponent(eventId)}/acknowledge`, {
    method: "POST",
    signal: options.signal,
  });
}

export async function dismissFoodAlertEvent(eventId, options = {}) {
  return requestJson(`${BASE}/alerts/events/${encodeURIComponent(eventId)}/dismiss`, {
    method: "POST",
    signal: options.signal,
  });
}

export async function evaluateFoodAlerts(payload = {}, options = {}) {
  return requestJson(`${BASE}/alerts/evaluate`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodNotificationPreferences(options = {}) {
  return requestJson(`${BASE}/notification-preferences`, { signal: options.signal });
}

export async function updateFoodNotificationPreferences(payload, options = {}) {
  return requestJson(`${BASE}/notification-preferences`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function generateFoodRecommendations(payload = {}, options = {}) {
  const path = payload.scope === "USER" ? `${BASE}/recommendations/generate/me` : `${BASE}/recommendations/generate`;
  return requestJson(path, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodRecommendations(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/recommendations${qs ? `?${qs}` : ""}`, { signal: options.signal });
}

export async function fetchFoodRecommendationById(recommendationId, options = {}) {
  return requestJson(`${BASE}/recommendations/${encodeURIComponent(recommendationId)}`, {
    signal: options.signal,
  });
}

export async function fetchFoodRecommendationSummary(params = {}, options = {}) {
  const qs = buildComparisonSearch(params);
  return requestJson(`${BASE}/recommendations/summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function dismissFoodRecommendation(recommendationId, options = {}) {
  return requestJson(`${BASE}/recommendations/${encodeURIComponent(recommendationId)}/dismiss`, {
    method: "POST",
    signal: options.signal,
  });
}

export async function saveFoodRecommendation(recommendationId, options = {}) {
  return requestJson(`${BASE}/recommendations/${encodeURIComponent(recommendationId)}/save`, {
    method: "POST",
    signal: options.signal,
  });
}

export async function fetchFoodLatestNormalizedPrices(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.productId) search.set("productId", params.productId);
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.minConfidence != null) search.set("minConfidence", String(params.minConfidence));
  if (params.status) search.set("status", params.status);
  const qs = search.toString();
  return requestJson(`${BASE}/retail-prices/normalized/latest${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodOsmStoreNormalizationSummary(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  const qs = search.toString();
  return requestJson(`${BASE}/osm-poi/normalization/summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodStoreReviewQueue(params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.sourceKey) search.set("sourceKey", params.sourceKey);
  if (params.reviewStatus) search.set("reviewStatus", params.reviewStatus);
  if (params.brand) search.set("brand", params.brand);
  if (params.city) search.set("city", params.city);
  if (params.issueType) search.set("issueType", params.issueType);
  if (params.duplicateCandidatesOnly) search.set("duplicateCandidatesOnly", "true");
  if (params.unknownBrandOnly) search.set("unknownBrandOnly", "true");
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  const qs = search.toString();
  return requestJson(`${BASE}/stores/review-queue${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function postFoodStoreReviewApprove(storeId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/review-queue/${encodeURIComponent(storeId)}/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodStoreReviewReject(storeId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/review-queue/${encodeURIComponent(storeId)}/reject`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodStoreReviewMarkDuplicate(storeId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/review-queue/${encodeURIComponent(storeId)}/mark-duplicate`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function patchFoodStoreReviewItem(storeId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/review-queue/${encodeURIComponent(storeId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

function buildDataQualityQuery(params = {}) {
  const search = new URLSearchParams();
  const keys = [
    "sourceKey",
    "sourceType",
    "issueType",
    "severity",
    "brand",
    "municipality",
    "reviewStatus",
    "reconciliationStatus",
    "geocodeStatus",
    "dedupeStatus",
    "sort",
    "affectsMapVisibility",
    "affectsPublicMap",
    "affectsAccessScoring",
    "limit",
    "offset",
  ];
  for (const key of keys) {
    if (params[key] != null && params[key] !== "") {
      search.set(key, String(params[key]));
    }
  }
  if (params.duplicateCandidatesOnly) search.set("duplicateCandidatesOnly", "true");
  if (params.conflictsOnly) search.set("conflictsOnly", "true");
  if (params.lowConfidenceGeocodesOnly) search.set("lowConfidenceGeocodesOnly", "true");
  if (params.missingBrandOnly) search.set("missingBrandOnly", "true");
  if (params.unknownBrandOnly) search.set("unknownBrandOnly", "true");
  if (params.missingGeometryOnly) search.set("missingGeometryOnly", "true");
  if (params.outOfBoundsOnly) search.set("outOfBoundsOnly", "true");
  if (params.includeRejected) search.set("includeRejected", "true");
  return search.toString();
}

export async function fetchFoodStoreDataQualityTriageProgress(params = {}, options = {}) {
  const qs = buildDataQualityQuery(params);
  return requestJson(`${BASE}/stores/data-quality/triage-progress${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function postFoodStoreDataQualityBulkAction(payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/data-quality/bulk-actions`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodStoreDataQualitySummary(params = {}, options = {}) {
  const qs = buildDataQualityQuery(params);
  return requestJson(`${BASE}/stores/data-quality/summary${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodStoreDataQualityReviewQueue(params = {}, options = {}) {
  const qs = buildDataQualityQuery(params);
  return requestJson(`${BASE}/stores/data-quality/review-queue${qs ? `?${qs}` : ""}`, {
    signal: options.signal,
  });
}

export async function fetchFoodStoreDataQualityReviewItem(storeId, options = {}) {
  return requestJson(`${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}`, {
    signal: options.signal,
  });
}

export async function fetchFoodStoreDataQualityReviewHistory(storeId, params = {}, options = {}) {
  const search = new URLSearchParams();
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  const qs = search.toString();
  return requestJson(
    `${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}/history${qs ? `?${qs}` : ""}`,
    { signal: options.signal }
  );
}

export async function postFoodStoreDataQualityDecision(storeId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}/decision`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodStoreDataQualityApprove(storeId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodStoreDataQualityReject(storeId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}/reject`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodStoreDataQualityMarkDuplicate(storeId, payload = {}, options = {}) {
  return requestJson(
    `${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}/mark-duplicate`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  );
}

export async function postFoodStoreDataQualityAssignBrand(storeId, payload = {}, options = {}) {
  return requestJson(
    `${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}/assign-brand`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  );
}

export async function postFoodStoreDataQualityCorrectGeometry(storeId, payload = {}, options = {}) {
  return requestJson(
    `${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}/correct-geometry`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  );
}

export async function postFoodStoreDataQualityMarkInactive(storeId, payload = {}, options = {}) {
  return requestJson(
    `${BASE}/stores/data-quality/review-queue/${encodeURIComponent(storeId)}/mark-inactive`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  );
}

export async function fetchFoodChainBranchSummary(options = {}) {
  return requestJson(`${BASE}/chain-branches/summary`, { signal: options.signal });
}

export async function fetchFoodChainBranchLocatorStrategy(options = {}) {
  return requestJson(`${BASE}/chain-branches/locator-strategy`, { signal: options.signal });
}

export async function postFoodBranchLocatorImportDryRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/branch-locators/import/dry-run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodBranchLocatorImportRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/branch-locators/import/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodBranchLocatorImportBatches(params = {}, options = {}) {
  const query = new URLSearchParams();
  if (params.sourceKey) query.set("sourceKey", params.sourceKey);
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return requestJson(`${BASE}/branch-locators/import/batches${suffix}`, { signal: options.signal });
}

export async function fetchFoodBranchLocatorImportBatch(batchId, options = {}) {
  return requestJson(`${BASE}/branch-locators/import/batches/${encodeURIComponent(batchId)}`, {
    signal: options.signal,
  });
}

export async function fetchFoodBranchLocatorImportStatus(options = {}) {
  return requestJson(`${BASE}/branch-locators/import/status`, { signal: options.signal });
}

export async function postFoodStoreReconciliationDryRun(payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/reconciliation/dry-run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodStoreReconciliationApply(payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/reconciliation/apply`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function fetchFoodStoreReconciliationSummary(options = {}) {
  return requestJson(`${BASE}/stores/reconciliation/summary`, { signal: options.signal });
}

export async function fetchFoodMapStoreReconciliationSummary(options = {}) {
  return requestJson(`${BASE}/map/store-reconciliation-summary`, { signal: options.signal });
}

export async function fetchFoodStoreReconciliationReviewQueue(params = {}, options = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.brand) query.set("brand", params.brand);
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return requestJson(`${BASE}/stores/reconciliation/review-queue${suffix}`, { signal: options.signal });
}

export async function postFoodStoreReconciliationApprove(matchId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/reconciliation/${encodeURIComponent(matchId)}/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodStoreReconciliationReject(matchId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/reconciliation/${encodeURIComponent(matchId)}/reject`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodStoreReconciliationSplit(matchId, payload = {}, options = {}) {
  return requestJson(`${BASE}/stores/reconciliation/${encodeURIComponent(matchId)}/split`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodChainBranchImport(payload = {}, options = {}) {
  return requestJson(`${BASE}/chain-branches/import/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}

export async function postFoodChainOsmReconciliation(payload = {}, options = {}) {
  return requestJson(`${BASE}/chain-branches/reconciliation/run`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
}
